import { useState, useEffect, useCallback } from "react";
import {
  Drawer,
  Button,
  Space,
  Tag,
  message,
  Modal,
  Form,
  Select,
  Upload,
  Spin,
  Empty,
  Image,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  UploadOutlined,
  UnlockOutlined,
  FilePdfOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import {
  listRiderDocuments,
  approveDocument,
  rejectDocument,
  replaceDocument,
  adminUploadDocument,
  resetDocumentLock,
  downloadDocumentFile,
} from "../../services/riderDocuments.service";

const STATUS_COLORS = {
  pending: { color: "#c2410c", bg: "#fff7ed", border: "#fed7aa" },
  approved: { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  rejected: { color: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
  notUploaded: { color: "#6b7280", bg: "#f3f4f6", border: "#e5e7eb" },
};

function StatusBadge({ status }) {
  const style = STATUS_COLORS[status] || STATUS_COLORS.notUploaded;
  const label =
    status === "notUploaded"
      ? "Not Submitted"
      : status
        ? status.charAt(0).toUpperCase() + status.slice(1)
        : "—";
  return (
    <Tag
      style={{
        color: style.color,
        background: style.bg,
        borderColor: style.border,
        fontWeight: 600,
      }}
    >
      {label}
    </Tag>
  );
}

function DocPreview({ doc }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let revoked = null;
    if (!doc?._id) {
      setUrl(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    downloadDocumentFile(doc._id)
      .then((raw) => {
        if (cancelled) return;
        const blob =
          raw instanceof Blob
            ? raw
            : new Blob([raw], { type: doc.mimeType || "application/octet-stream" });
        const objectUrl = URL.createObjectURL(blob);
        revoked = objectUrl;
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [doc?._id, doc?.mimeType, doc?.updatedAt]);

  if (!doc?._id) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No file uploaded" />;
  }
  if (loading) return <Spin size="small" />;
  if (!url) return <span style={{ color: "#94a3b8" }}>Preview unavailable</span>;

  const isPdf =
    (doc.mimeType || "").includes("pdf") ||
    (doc.fileName || "").toLowerCase().endsWith(".pdf");

  if (isPdf) {
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <Button icon={<FilePdfOutlined />}>Open PDF</Button>
      </a>
    );
  }

  return (
    <Image
      src={url}
      alt={doc.documentTypeLabel || "Document"}
      style={{ maxHeight: 160, objectFit: "contain" }}
    />
  );
}

/**
 * Drawer to review / approve / reject / replace documents for one partner.
 */
export default function DocumentDetailDrawer({ open, partner, onClose, onChanged }) {
  const { canApprove, canEdit } = usePermission("deliveryPartners");
  const { options: reasonOptions } = useSettingsDropdown("reasons", {
    filter: (item) =>
      !item.applicableModule ||
      item.applicableModule === "deliveryPartners" ||
      item.applicableModule === "all",
  });

  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [rejectModal, setRejectModal] = useState({ open: false, doc: null });
  const [rejectForm] = Form.useForm();
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDocs = useCallback(async () => {
    if (!partner?._id) return;
    setLoading(true);
    try {
      const res = await listRiderDocuments(partner._id);
      setDocuments(res?.data?.documents || []);
    } catch {
      message.error("Failed to load documents");
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [partner?._id]);

  useEffect(() => {
    if (open) fetchDocs();
  }, [open, fetchDocs]);

  const refresh = async () => {
    await fetchDocs();
    onChanged?.();
  };

  const handleApprove = async (doc) => {
    try {
      setActionLoading(true);
      await approveDocument(doc._id);
      message.success("Document approved");
      await refresh();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Approve failed");
    } finally {
      setActionLoading(false);
    }
  };

  const openReject = (doc) => {
    rejectForm.resetFields();
    setRejectModal({ open: true, doc });
  };

  const handleRejectSubmit = async () => {
    try {
      const values = await rejectForm.validateFields();
      setActionLoading(true);
      await rejectDocument(rejectModal.doc._id, {
        rejectionReason: values.rejectionReason,
      });
      message.success("Document rejected");
      setRejectModal({ open: false, doc: null });
      await refresh();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.errors?.[0]?.msg || "Reject failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReplace = async (doc, file) => {
    try {
      setActionLoading(true);
      if (doc._id) {
        await replaceDocument(doc._id, file);
        message.success("Document replaced — pending re-approval");
      } else {
        await adminUploadDocument(partner._id, doc.documentType, file);
        message.success("Document uploaded — pending approval");
      }
      await refresh();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Upload failed");
    } finally {
      setActionLoading(false);
    }
    return false;
  };

  const handleReset = async (doc) => {
    try {
      setActionLoading(true);
      await resetDocumentLock(doc._id);
      message.success("Lock reset — rider can re-upload");
      await refresh();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Reset failed");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <Drawer
        title={
          partner
            ? `Documents — ${partner.fullName || ""} (${partner.riderId || ""})`
            : "Documents"
        }
        width={560}
        open={open}
        onClose={onClose}
        destroyOnClose
        extra={
          <Button icon={<ReloadOutlined />} onClick={fetchDocs} loading={loading}>
            Refresh
          </Button>
        }
      >
        <Spin spinning={loading || actionLoading}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            {documents.map((doc) => (
              <div
                key={doc.documentType}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  padding: 16,
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                  }}
                >
                  <strong>{doc.documentTypeLabel || doc.documentType}</strong>
                  <StatusBadge status={doc.status} />
                </div>

                <DocPreview doc={doc.status === "notUploaded" ? null : doc} />

                {doc.status === "rejected" && doc.rejectionReasonText && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#b91c1c" }}>
                    Reason: {doc.rejectionReasonText}
                  </div>
                )}

                {doc.isLocked && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                    Locked for rider edits
                  </div>
                )}

                <Space wrap style={{ marginTop: 12 }}>
                  {canApprove && doc.status === "pending" && doc._id && (
                    <>
                      <Button
                        type="primary"
                        size="small"
                        icon={<CheckOutlined />}
                        style={{ background: "#15803d", borderColor: "#15803d" }}
                        onClick={() => handleApprove(doc)}
                      >
                        Approve
                      </Button>
                      <Button
                        danger
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => openReject(doc)}
                      >
                        Reject
                      </Button>
                    </>
                  )}

                  {canEdit && (
                    <Upload
                      accept={
                        doc.documentType === "profilePhoto"
                          ? ".jpg,.jpeg,.png"
                          : ".jpg,.jpeg,.png,.pdf"
                      }
                      showUploadList={false}
                      beforeUpload={(file) => {
                        if (file.size > 1024 * 1024) {
                          message.error("File must be 1 MB or less");
                          return Upload.LIST_IGNORE;
                        }
                        handleReplace(doc, file);
                        return false;
                      }}
                    >
                      <Button size="small" icon={<UploadOutlined />}>
                        {doc._id ? "Replace" : "Upload"}
                      </Button>
                    </Upload>
                  )}

                  {canEdit && doc._id && doc.isLocked && (
                    <Popconfirm
                      title="Reset lock so rider can re-upload?"
                      onConfirm={() => handleReset(doc)}
                    >
                      <Tooltip title="Reset edit lock">
                        <Button size="small" icon={<UnlockOutlined />}>
                          Reset Lock
                        </Button>
                      </Tooltip>
                    </Popconfirm>
                  )}
                </Space>
              </div>
            ))}

            {!loading && documents.length === 0 && (
              <Empty description="No document slots" />
            )}
          </Space>
        </Spin>
      </Drawer>

      <Modal
        title="Reject Document"
        open={rejectModal.open}
        onCancel={() => setRejectModal({ open: false, doc: null })}
        onOk={handleRejectSubmit}
        okText="Reject"
        okButtonProps={{ danger: true, loading: actionLoading }}
        destroyOnClose
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="rejectionReason"
            label="Rejection Reason"
            rules={[{ required: true, message: "Select a rejection reason" }]}
          >
            <Select
              placeholder="Select reason"
              options={reasonOptions}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
